import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import PageTemplate from '../components/PageTemplate/PageTemplate';
import { P } from '../components/ui';
import { recordSessionAuditAction } from '../service/form';
import { getSelectedFormIdFromSession } from '../utils/form-id';
import { getTenantFromPath } from '../utils/tenant-utils';
import { useAuthContext } from './AuthContext';
import { resolvePostLoginTarget } from './postLogin';
import { SigningIn } from './RequireAuth';

type OidcState = { returnTo?: unknown } | undefined;

const returnToFrom = (state: unknown): string => {
  const candidate = (state as OidcState)?.returnTo;
  return typeof candidate === 'string' ? candidate : '';
};

/**
 * Lands here from the GRID hosted UI with ?code=&state=. react-oidc-context
 * completes the PKCE exchange; this page then sends the user where they were
 * going, which rode along in the OIDC state as `returnTo`.
 */
export default function AuthCallback() {
  const auth = useAuth();
  const { fetchJWT } = useAuthContext();
  const navigate = useNavigate();
  const handled = useRef(false);
  const [failure, setFailure] = useState<string>('');

  const errorReturnTo = auth.error ? returnToFrom((auth.error as { state?: unknown }).state) : '';

  useEffect(() => {
    if (handled.current || auth.isLoading) return;
    if (auth.error) {
      setFailure(auth.error.message || 'Sign-in failed');
      return;
    }
    if (!auth.isAuthenticated) return;
    handled.current = true;

    const returnTo = returnToFrom(auth.user?.state);
    const tenant = getTenantFromPath(returnTo);
    const finish = async () => {
      if (!tenant) {
        navigate('/error', { replace: true });
        return;
      }
      try {
        const { idToken } = await fetchJWT({ redirectOnFail: false });
        if (idToken) {
          const selectedFormId = getSelectedFormIdFromSession(tenant);
          await recordSessionAuditAction(
            idToken,
            'login',
            selectedFormId ? { form_id: selectedFormId } : undefined,
            { 'X-Tenant': tenant },
          );
        }
      } catch (_err) {
        // Session audit must never block sign-in.
      }
      const target = await resolvePostLoginTarget({ tenant, returnTo, fetchJWT });
      navigate(target, { replace: true });
    };
    void finish();
  }, [auth.error, auth.isAuthenticated, auth.isLoading, auth.user, fetchJWT, navigate]);

  if (failure) {
    return (
      <PageTemplate title="Sign-in failed">
        <P>{failure}</P>
        <button
          type="button"
          className="govuk-button"
          onClick={() => void auth.signinRedirect({ state: { returnTo: errorReturnTo || '/' } })}
        >
          Try again
        </button>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <SigningIn />
    </PageTemplate>
  );
}
